import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import authService from '../../services/authService';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!tokenFromUrl) {
      setError('Invalid or missing link. Please request a new one.');
      return;
    }

    setIsSubmitting(true);
    try {
      const isSetupFlow = window.location.pathname.includes('setup-password');

      let result;
      if (isSetupFlow) {
        // Invite Flow requires confirm_password
        result = await authService.setupPassword({
          token: tokenFromUrl,
          password,
          confirm_password: confirmPassword,
        });
      } else {
        result = await authService.resetPassword({
          token: tokenFromUrl,
          password,
          confirm_password: confirmPassword,
        });
      }

      if (result?.success) {
        toast.success(result.message ?? 'Password updated. You can sign in now.');
        navigate('/login', { replace: true });
      } else {
        const msg = result?.message ?? 'Failed to set password.';
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        data?.error ||
        err?.message ||
        'Something went wrong. Please try again or request a new reset link.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!tokenFromUrl) {
    return (
      <div className="w-full max-w-md px-4 py-8 sm:py-12">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">
            Set new password
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            This page is for setting a new password after you click the link in your email.
            If you don’t have a link, request one below.
          </p>
          <Link
            to="/forgot-password"
            className="mt-4 inline-block rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-hover"
          >
            Request reset link
          </Link>
          <p className="mt-4 text-center">
            <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-hover">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md px-4 py-8 sm:py-12">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
        <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          Set new password
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Enter your new password below.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              New password
            </label>
            <div className="relative mt-1.5">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
              Confirm password
            </label>
            <div className="relative mt-1.5">
              <input
                id="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((s) => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60"
          >
            {isSubmitting ? 'Updating…' : 'Set password'}
          </button>

          <p className="text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-primary hover:text-primary-hover"
            >
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
